import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Input } from 'reactstrap';
import getMenuData from 'Layouts/LayoutMenuData';
import { APIClient } from '../../helpers/api_helper';

//SimpleBar
import SimpleBar from "simplebar-react";

interface SearchResult {
    label: string;
    link: string;
    icon?: string;
    type: string;
    statusBadge?: React.ReactNode;
    typeBadge?: React.ReactNode;
}

const ESTIMATE_STATUS_MAPPING: { [key: number]: { description: string; color: string } } = {
    1: { description: 'Angeboten', color: 'primary' },
    2: { description: 'Angenommen', color: 'success' },
    3: { description: 'Abgelehnt', color: 'secondary' },
    4: { description: 'Gestorben', color: 'danger' }
};

const CALC_STATUS_MAPPING: { [key: number]: { description: string; color: string } } = {
    1: { description: 'offen', color: 'success' },
    2: { description: 'abgeschlossen', color: 'info' },
    3: { description: 'archiv', color: 'dark' },
    4: { description: 'gestorben', color: 'danger' }
};

const INVOICE_STATUS_MAPPING: { [key: string]: { description: string; color: string } } = {
    Paid: { description: 'Bezahlt', color: 'success' },
    Unpaid: { description: 'Offen', color: 'warning' },
    Cancel: { description: 'Storniert', color: 'danger' },
    Refund: { description: 'Erstattet', color: 'primary' },
    Active: { description: 'Aktiv', color: 'success' },
    Inactive: { description: 'Inaktiv', color: 'secondary' }
};

const SearchOption = () => {
    const navData = useMemo(() => getMenuData(), []);
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [filterData, setFilterData] = useState<Array<{
        label: string;
        link: string;
        icon?: string;
        type?: string;
        subItems?: any[];
    }>>([]);
    const [projects, setProjects] = useState<any[]>([]);
    const [users, setUsers] = useState<any[]>([]);
    const [estimates, setEstimates] = useState<any[]>([]);
    const [calculations, setCalculations] = useState<any[]>([]);
    const [invoices, setInvoices] = useState<any[]>([]);
    const api = useMemo(() => new APIClient(), []);
    const [projectSearchTerm, setProjectSearchTerm] = useState('');
    const [projectSearchLoading, setProjectSearchLoading] = useState(false);
    const [estimatesLoading, setEstimatesLoading] = useState(false);
    const [calculationsLoading, setCalculationsLoading] = useState(false);
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [usersLoading, setUsersLoading] = useState(false);
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [allResults, setAllResults] = useState<SearchResult[]>([]);
    const searchDropdownRef = useRef<HTMLDivElement>(null);

    // Lade User beim Mount (nur aktive User)
    useEffect(() => {
        api.get('/users', { pageSize: 1000 })
            .then(res => {
                const usersData = Array.isArray(res.data) ? res.data : (res.data.data ? res.data.data : []);
                // Filter nur aktive User (active = 1 oder true)
                const activeUsers = usersData.filter((user: any) => user.active === 1 || user.active === true);
                setUsers(activeUsers);
            })
            .catch(err => {
                console.error('Fehler beim Laden der User:', err);
                setUsers([]);
            });
    }, [api]);

    useEffect(() => {
        const searchOptions = document.getElementById("search-close-options") as HTMLElement;
        const dropdown = document.getElementById("search-dropdown") as HTMLElement;
        const searchInput = document.getElementById("search-options") as HTMLInputElement;

        if (!searchOptions || !dropdown || !searchInput) return;

        const handleSearchInput = () => {
            const inputLength = searchInput.value.length;
            if (inputLength > 0) {
                dropdown.classList.add("show");
                searchOptions.classList.remove("d-none");
            } else {
                dropdown.classList.remove("show");
                searchOptions.classList.add("d-none");
            }
        };

        const handleSearchClose = () => {
            searchInput.value = "";
            dropdown.classList.remove("show");
            searchOptions.classList.add("d-none");
        };

        const handleBodyClick = (e: any) => {
            // Check if e.target is an Element before calling getAttribute
            if (e.target && e.target instanceof Element && e.target.getAttribute('id') !== "search-options") {
                dropdown.classList.remove("show");
                searchOptions.classList.add("d-none");
            }
        };

        searchInput.addEventListener("focus", handleSearchInput);
        searchInput.addEventListener("keyup", handleSearchInput);
        searchOptions.addEventListener("click", handleSearchClose);
        document.body.addEventListener("click", handleBodyClick);

        // Cleanup function to remove event listeners
        return () => {
            searchInput.removeEventListener("focus", handleSearchInput);
            searchInput.removeEventListener("keyup", handleSearchInput);
            searchOptions.removeEventListener("click", handleSearchClose);
            document.body.removeEventListener("click", handleBodyClick);
        };
    }, []); // Empty dependency array - only run once on mount

    const onKeyDownPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const { key } = e;
        
        if (key === "ArrowDown") {
            e.preventDefault();
            setSelectedIndex(prev => {
                const newIndex = prev + 1;
                return newIndex >= allResults.length ? 0 : newIndex;
            });
        } else if (key === "ArrowUp") {
            e.preventDefault();
            setSelectedIndex(prev => {
                const newIndex = prev - 1;
                return newIndex < 0 ? allResults.length - 1 : newIndex;
            });
        } else if (key === "Enter") {
            e.preventDefault();
            if (selectedIndex >= 0 && selectedIndex < allResults.length) {
                const selectedResult = allResults[selectedIndex];
                navigate(selectedResult.link);
                // Close search dropdown
                const dropdown = document.getElementById("search-dropdown");
                const searchOptions = document.getElementById("search-close-options");
                const searchInput = document.getElementById("search-options") as HTMLInputElement;
                if (dropdown) dropdown.classList.remove("show");
                if (searchOptions) searchOptions.classList.add("d-none");
                if (searchInput) {
                    searchInput.value = "";
                    searchInput.blur();
                }
                setSearchTerm("");
                setSelectedIndex(-1);
            }
        }
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        setSearchTerm(event.target.value);
        setProjectSearchTerm(event.target.value);
        setSelectedIndex(-1); // Reset selection when search term changes
    };

    // Filter routes based on search term
    useEffect(() => {
        const filteredMenuItems = navData.reduce((result: any, menuItem: any) => {
            const lowercaseLabel = menuItem.label ? menuItem.label.toLowerCase() : '';
            const lowercaseLink = menuItem.link ? menuItem.link.toLowerCase() : '';
            const searchLower = searchTerm.toLowerCase();

            // Check if main menu item matches
            const mainItemMatches = lowercaseLabel.includes(searchLower) || lowercaseLink.includes(searchLower);

            // Check if any sub items match
            const filteredSubItems = (menuItem.subItems || []).filter((subItem: any) => {
                const lowercaseSubItemLabel = subItem.label ? subItem.label.toLowerCase() : '';
                const lowercaseSubItemLink = subItem.link ? subItem.link.toLowerCase() : '';

                return (
                    lowercaseSubItemLabel.includes(searchLower) ||
                    lowercaseSubItemLink.includes(searchLower)
                );
            });

            // Only add once: either with filtered subItems or as main item
            if (mainItemMatches && filteredSubItems.length === 0) {
                // Main item matches but no sub items match
                result.push(menuItem);
            } else if (filteredSubItems.length > 0) {
                // Sub items match (regardless of whether main item matches)
                const menuItemWithSubItems = { ...menuItem, subItems: filteredSubItems };
                result.push(menuItemWithSubItems);
            }

            return result;
        }, []);

        setFilterData(filteredMenuItems);
    }, [searchTerm, navData]);

    // Serverseitige Projektsuche + Estimates, Calculations, Invoices, Users
    useEffect(() => {
        if (projectSearchTerm.length < 2) {
            setProjects([]);
            setEstimates([]);
            setCalculations([]);
            setInvoices([]);
            // Users are already loaded in the initial useEffect, no need to reload
            return;
        }
        setProjectSearchLoading(true);
        setEstimatesLoading(true);
        setCalculationsLoading(true);
        setInvoicesLoading(true);
        setUsersLoading(true);
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
            // Projekte
            api.get('/projects', { search: projectSearchTerm, page: 1, pageSize: 15 })
                .then(res => {
                    const projectsData = Array.isArray(res.data) ? res.data : (res.data.data ? res.data.data : []);
                    setProjects(projectsData);
                })
                .catch(err => {
                    console.error('Fehler beim Suchen der Projekte:', err);
                    setProjects([]);
                })
                .finally(() => setProjectSearchLoading(false));
            
            // Estimates
            api.get('/estimates', { search: projectSearchTerm, page: 1, pageSize: 10 })
                .then(res => {
                    const estimatesData = Array.isArray(res.data) ? res.data : (res.data.data ? res.data.data : []);
                    setEstimates(estimatesData);
                })
                .catch(err => {
                    console.error('Fehler beim Suchen der Kostenvoranschläge:', err);
                    setEstimates([]);
                })
                .finally(() => setEstimatesLoading(false));
            
            // Calculations
            api.get('/calculations', { search: projectSearchTerm, page: 1, pageSize: 10 })
                .then(res => {
                    const calculationsData = Array.isArray(res.data) ? res.data : (res.data.data ? res.data.data : []);
                    setCalculations(calculationsData);
                })
                .catch(err => {
                    console.error('Fehler beim Suchen der Kalkulationen:', err);
                    setCalculations([]);
                })
                .finally(() => setCalculationsLoading(false));
            
            // Invoices
            api.get('/invoices', { search: projectSearchTerm, page: 1, pageSize: 10 })
                .then(res => {
                    const invoicesData = Array.isArray(res.data) ? res.data : (res.data.data ? res.data.data : []);
                    setInvoices(invoicesData);
                })
                .catch(err => {
                    console.error('Fehler beim Suchen der Rechnungen:', err);
                    setInvoices([]);
                })
                .finally(() => setInvoicesLoading(false));
            
            // Users - ersetze mit Suchergebnissen statt zu mergen (nur aktive User)
            api.get('/users', { search: projectSearchTerm, page: 1, pageSize: 15 })
                .then(res => {
                    const usersData = Array.isArray(res.data) ? res.data : (res.data.data ? res.data.data : []);
                    // Filter nur aktive User (active = 1 oder true)
                    const activeUsers = usersData.filter((user: any) => user.active === 1 || user.active === true);
                    setUsers(activeUsers);
                })
                .catch(err => {
                    console.error('Fehler beim Suchen der Users:', err);
                    setUsers([]);
                })
                .finally(() => setUsersLoading(false));
        }, 300);

        // Cleanup function
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [projectSearchTerm, api]);

    // Hilfsfunktionen für Status-Badges
    const getProjectStatusBadge = (status: string) => {
        const lower = (status || '').toLowerCase();
        if (lower === 'open') return <span className="badge bg-success">Open</span>;
        if (lower === 'closed') return <span className="badge bg-secondary">Closed</span>;
        if (lower === 'archived') return <span className="badge bg-danger">Archived</span>;
        return <span className="badge bg-light text-dark">{status}</span>;
    };

    // Collect all search results whenever data changes
    useEffect(() => {
        const results: SearchResult[] = [];

        // Menu items
        filterData.forEach((menuItem) => {
            if (!menuItem.subItems) {
                results.push({
                    label: menuItem.label,
                    link: menuItem.link,
                    icon: menuItem.icon,
                    type: 'menu'
                });
            }
            // SubItems
            if (menuItem.subItems && menuItem.subItems.length > 0) {
                menuItem.subItems.filter((subItem: any) => {
                    return subItem.label !== menuItem.label;
                }).forEach((subItem: any) => {
                    results.push({
                        label: subItem.label,
                        link: subItem.link,
                        icon: menuItem.icon,
                        type: 'menu'
                    });
                });
            }
        });

        // Users
        const filteredUsers = users.filter((user: any) =>
            (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (user.username && user.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        filteredUsers.forEach((user: any) => {
            results.push({
                label: user.full_name || user.username || user.email,
                link: `/users/${user.id}`,
                icon: 'ri-user-line',
                type: 'user',
                typeBadge: <span className="badge bg-soft-primary text-primary ms-2">User</span>
            });
        });

        // Projects
        const filteredProjects = projects.filter((project: any) =>
            project.title &&
            project.title.toLowerCase().includes(searchTerm.toLowerCase())
        );
        filteredProjects.forEach((project: any) => {
            results.push({
                label: project.title,
                link: `/projects/${project.id}`,
                icon: 'ri-folder-line',
                type: 'project',
                statusBadge: <span className="ms-2">{getProjectStatusBadge(project.status_name || project.status || '')}</span>,
                typeBadge: <span className="badge bg-soft-info text-info ms-2">Projekt</span>
            });
        });

        // Estimates
        const filteredEstimates = estimates.filter((estimate: any) =>
            (estimate.num && estimate.num.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (estimate.project_title && estimate.project_title.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        filteredEstimates.forEach((estimate: any) => {
            const statusInfo = ESTIMATE_STATUS_MAPPING[estimate.status_id] || { description: 'Unbekannt', color: 'secondary' };
            results.push({
                label: `${estimate.num ? `${estimate.num} ` : ''}${estimate.project_title ? (estimate.num ? `– ${estimate.project_title}` : estimate.project_title) : ''}`,
                link: `/estimates/${estimate.id}`,
                icon: 'ri-file-list-3-line',
                type: 'estimate',
                statusBadge: <span className={`badge bg-soft-${statusInfo.color} text-${statusInfo.color} ms-2`}>{statusInfo.description}</span>,
                typeBadge: <span className="badge bg-soft-primary text-primary ms-2">KV</span>
            });
        });

        // Calculations
        const filteredCalculations = calculations.filter((calc: any) =>
            (calc.name && calc.name.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        filteredCalculations.forEach((calc: any) => {
            const statusInfo = CALC_STATUS_MAPPING[calc.status_id] || { description: 'Unbekannt', color: 'secondary' };
            results.push({
                label: calc.name,
                link: `/calculations/${calc.id}`,
                icon: 'ri-calculator-line',
                type: 'calculation',
                statusBadge: <span className={`badge bg-soft-${statusInfo.color} text-${statusInfo.color} ms-2`}>{statusInfo.description}</span>,
                typeBadge: <span className="badge bg-soft-warning text-warning ms-2">Kalkulation</span>
            });
        });

        // Invoices
        const filteredInvoices = invoices.filter((inv: any) =>
            (inv.nummer && inv.nummer.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (inv.empfaenger && inv.empfaenger.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (inv.project_title && inv.project_title.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        filteredInvoices.forEach((inv: any) => {
            let statusKey = inv.status || (inv.is_paid ? 'Paid' : 'Unpaid');
            const statusInfo = INVOICE_STATUS_MAPPING[statusKey] || { description: statusKey || 'Unbekannt', color: 'secondary' };
            results.push({
                label: `${inv.nummer} ${inv.empfaenger ? `– ${inv.empfaenger}` : ''} ${inv.project_title ? `– ${inv.project_title}` : ''}`,
                link: `/invoices/${inv.id}`,
                icon: 'ri-file-text-line',
                type: 'invoice',
                statusBadge: <span className={`badge bg-soft-${statusInfo.color} text-${statusInfo.color} ms-2`}>{statusInfo.description}</span>,
                typeBadge: <span className="badge bg-soft-danger text-danger ms-2">Rechnung</span>
            });
        });

        setAllResults(results);
    }, [filterData, users, projects, estimates, calculations, invoices, searchTerm]);

    // Add effect to scroll selected item into view
    useEffect(() => {
        if (selectedIndex >= 0 && searchDropdownRef.current) {
            const selectedElement = searchDropdownRef.current.querySelector(`[data-result-index="${selectedIndex}"]`);
            if (selectedElement) {
                selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }, [selectedIndex]);

    const renderResults = () => {
        let currentType = '';
        let resultIndex = 0;
        const groupedResults: JSX.Element[] = [];

        allResults.forEach((result) => {
            if (result.type !== currentType) {
                currentType = result.type;
                
                // Add section header
                const headerText = {
                    'menu': 'Navigation',
                    'user': 'User',
                    'project': 'Projekte',
                    'estimate': 'Kostenvoranschläge',
                    'calculation': 'Kalkulationen',
                    'invoice': 'Rechnungen'
                }[currentType] || currentType;

                groupedResults.push(
                    <div key={`header-${currentType}`} className="dropdown-header mt-2">
                        <h6 className="text-overflow text-muted mb-1 text-uppercase">{headerText}</h6>
                    </div>
                );
            }

            const isSelected = resultIndex === selectedIndex;
            const currentResultIndex = resultIndex;
            resultIndex++;

            groupedResults.push(
                <Link
                    key={`result-${currentResultIndex}`}
                    to={result.link}
                    className={`dropdown-item notify-item ${isSelected ? 'active' : ''}`}
                    data-result-index={currentResultIndex}
                    onMouseEnter={() => setSelectedIndex(currentResultIndex)}
                    style={isSelected ? { backgroundColor: '#f3f3f9' } : {}}
                >
                    <i className={`${result.icon} align-middle fs-xl text-muted me-2`}></i>
                    <span>{result.label}</span>
                    {result.statusBadge}
                    {result.typeBadge}
                </Link>
            );
        });

        // Add loading indicators
        if (projectSearchLoading || estimatesLoading || calculationsLoading || invoicesLoading || usersLoading) {
            groupedResults.push(
                <div key="loading" className="dropdown-item text-muted">
                    {projectSearchLoading && 'Suche Projekte... '}
                    {estimatesLoading && 'Suche Kostenvoranschläge... '}
                    {calculationsLoading && 'Suche Kalkulationen... '}
                    {invoicesLoading && 'Suche Rechnungen... '}
                    {usersLoading && 'Suche User... '}
                </div>
            );
        }

        return groupedResults;
    };

    return (
        <React.Fragment>
            <form className="app-search d-none d-md-block">
                <div className="position-relative">
                    <Input type="text" className="form-control" placeholder="Suche... (Strg+K)"
                        id="search-options"
                        value={searchTerm}
                        onKeyDown={onKeyDownPress}
                        onChange={handleChange}
                    />
                    <span className="mdi mdi-magnify search-widget-icon"></span>
                    <span className="mdi mdi-close-circle search-widget-icon search-widget-icon-close d-none"
                        id="search-close-options"></span>
                </div>
                <div className="dropdown-menu dropdown-menu-lg" id="search-dropdown" ref={searchDropdownRef}>
                    <SimpleBar style={{ maxHeight: "300px" }}>
                        {renderResults()}
                    </SimpleBar>

                    <div className="text-center pt-3 pb-1">
                        <Link to="/pages-search-results" className="btn btn-primary btn-sm">View All Results <i
                            className="ri-arrow-right-line ms-1"></i></Link>
                    </div>
                </div>
            </form>
        </React.Fragment>
    );
};

export default SearchOption;